/**
 * The operator-API RBAC matrix and the request-time check both the Node service and the `core`
 * Worker enforce (task T8.2, FR-12 AC2–AC4, AC6).
 *
 * Roles are a monotonic ladder `viewer < operator < admin`: every higher role may do everything a
 * lower one may, plus more. But the authority is NOT the ladder — it is the per-operation matrix
 * {@link REQUIRED_ROLE}, generated from `openapi.yaml`'s `x-required-role`, so each operation's
 * required role is declared once in the contract and read here by both build targets. The ladder is
 * only how a required role is compared with the caller's role.
 *
 * Why the matrix is the single source of truth (FR-12 AC6): the UI, the BFF and the Node service
 * must all agree on what a role may do. They cannot import each other (one is a Worker bundle, one
 * is a Node container), so they share this one generated table through `@oathera/contract`. A new
 * operation or a changed role is one edit to the OpenAPI document; `npm run contract:generate`
 * refreshes the matrix and the drift check (`openapi.test.ts`) fails if the committed matrix no
 * longer matches the document.
 *
 * `can` and `requireRole` are pure and total. They answer only "may this role perform this
 * operation"; tenancy (FR-12.5) is a separate check the handler and gateway already make. `system`
 * is not an operator-API role (design 3) and is never one of {@link OPERATOR_ROLES}; a `system`
 * envelope never reaches the API, so the matrix does not model it.
 */
import {
  OPERATOR_ROLES,
  REQUIRED_ROLE,
  type OperationId,
  type OperatorRole,
} from "../generated/required-roles.generated";

export { OPERATOR_ROLES, REQUIRED_ROLE };
export type { OperationId, OperatorRole };

/** The ladder: a role's rank, higher is more privileged. Derived from the order of the roles. */
const RANK: Readonly<Record<OperatorRole, number>> = Object.freeze(
  Object.fromEntries(OPERATOR_ROLES.map((r, i) => [r, i + 1])) as Record<OperatorRole, number>,
);

/** True when `value` is one of the operator-API roles (`viewer | operator | admin`). */
export function isOperatorRole(value: unknown): value is OperatorRole {
  return typeof value === "string" && (OPERATOR_ROLES as readonly string[]).includes(value);
}

/** True when `value` is a known operation id in the matrix. */
export function isOperationId(value: unknown): value is OperationId {
  return typeof value === "string" && value in REQUIRED_ROLE;
}

/** The minimum role an operation requires. */
export function requiredRole(operation: OperationId): OperatorRole {
  return REQUIRED_ROLE[operation];
}

/**
 * True when `role` is at least the operation's required role on the ladder — the one authorisation
 * decision. `role` is the verified envelope's role (`VerifiedEnvelope.role` from task T8.1); a
 * caller that reaches this with a non-operator role (only `system` is possible) is denied, because
 * `system` has no rank in the operator ladder.
 */
export function can(role: OperatorRole, operation: OperationId): boolean {
  return RANK[role] >= RANK[REQUIRED_ROLE[operation]];
}

/**
 * The decision as a tagged result, for a caller that wants the required role in the denial (to log
 * it or to shape a `403`). Narrows a possibly-`system` role: a role outside the operator ladder is
 * denied without a rank comparison.
 */
export type RoleDecision =
  | { readonly allowed: true }
  | { readonly allowed: false; readonly required: OperatorRole; readonly actual: string };

/**
 * Enforces the matrix for one operation. `role` is whatever the verified envelope carried, which is
 * `viewer | operator | admin | system`; only the three operator roles can ever be allowed, so a
 * `system` (or any non-operator) role is denied here rather than silently ranked.
 */
export function requireRole(role: string, operation: OperationId): RoleDecision {
  const required = REQUIRED_ROLE[operation];
  if (isOperatorRole(role) && can(role, operation)) return { allowed: true };
  return { allowed: false, required, actual: role };
}
